import {PlateIdTypes} from "../models/plateid-types-enum";
import {PlateStatus, ProcessStep} from "../models/process-step";
import {RedoType} from "../models/redo-type-enum";
import {SampleHistoryProcessSteps} from "../models/sampleHistoryProcessSteps";
import {Step} from "../models/step";
import {environment} from "../../environments/environment";

export const ALLOWED_ROLE_CLAIMS_FOR_HISTORY_CHANGES: string[] = [
    environment.AZURE_SUPER_USER_DK_GROUP_CLAIM,
    environment.AZURE_SUPER_USER_GROUP_CLAIM,
    environment.AZURE_MDM_GROUP_ID,
    environment.AZURE_MDM_DK_GROUP_ID,
];

export const METADATA_KEYS_FOR_PLATE = [
    "plateID",
    "sender",
    "type",
    "status",
    "creationDate",
    "sampleType",
    "processFlowName",
    "priority",
    "runId",
    "sourcePlates",
    "projectID",
    "projectName"
];

export const STEPS_WITH_NO_ACTION = [
    "PREPARATION_COMPLETED",
    "LYSIS_COMPLETED",
    "EXTRACTION_COMPLETED",
    "WGA_COMPLETED",
    "PGX_COMPLETED",
    "PREPCR_COMPLETED",
    "ALIQUOTATION_COMPLETED",
    "FRAGMENTATION_COMPLETED",
    "RECOMBINING_COMPLETED",
    "PRECIPITATION_COMPLETED",
    "RESUSPENDING_COMPLETED",
    "DENATURATION_COMPLETED",
    "HYBRIDIZATION_COMPLETED",
    "COMPLETLY_TRANSFERED",
];

export const MERGE_REDO_LIST = [
    {
        label: "Merge Redo",
        value: {},
    },
];

export const HAIR_REDO_LIST = [
    {
        label: "Lysis Redo",
        value: {
            redoLabel: "Lysis Redo",
            redo: RedoType.LYSIS_REDO,
            status: PlateStatus.PLATE_REDO,
            stepName: "",
            sampleType: "Hair",
        },
    },
    {
        label: "Extraction Redo",
        value: {
            redoLabel: "Extraction Redo",
            redo: RedoType.EXTRACTION_REDO,
            status: PlateStatus.LYSIS_STARTED,
            stepName: ProcessStep.EXTRACTION,
            sampleType: "Hair",
        },
    },
    {
        label: "Aliquotation Redo",
        value: {
            redoLabel: "Aliquotation Redo",
            redo: RedoType.ALIQUOTATION_REDO,
            status: PlateStatus.EXTRACTION_COMPLETED,
            stepName: ProcessStep.ALIQUOTATION,
            sampleType: "Hair",
        },
    },
];

export const EARTAG_REDO_LIST = [
    {
        label: "Extraction Redo",
        value: {
            redoLabel: "Extraction Redo",
            redo: RedoType.EXTRACTION_REDO,
            status: PlateStatus.LYSIS_COMPLETED,
            stepName: ProcessStep.EXTRACTION,
            sampleType: "Eartag",
        },
    },
    {
        label: "Aliquotation Redo",
        value: {
            redoLabel: "Aliquotation Redo",
            redo: RedoType.ALIQUOTATION_REDO,
            status: PlateStatus.EXTRACTION_COMPLETED,
            stepName: ProcessStep.ALIQUOTATION,
            sampleType: "Eartag",
        },
    },
];

export const TLR_REDO_LIST = [
    {
        label: "Aliquotation Redo",
        value: {
            redoLabel: "Aliquotation Redo",
            redo: RedoType.ALIQUOTATION_REDO,
            status: PlateStatus.EXTRACTION_COMPLETED,
            stepName: ProcessStep.ALIQUOTATION,
            sampleType: "TLR",
        },
    },
];

export const ALWAYS_VISIBLE_SAMPLE_KEYS = ["announced", "activated"];

export const DATES_TO_FORMAT = [
    "announcedDate",
    "lastModifiedDate",
    "creationDate",
    "boxReceptionTimeStamp",
    "sampleUpdatedDate",
    "scanDate",
    "birthDate",
    "dueDate",
    "requestedDate",
    "sampleCreateDate",
    "tatStartWeatherbys",
    "closedDate",
    "holdDate",
    "sampleCompletionDate",
];

export const KEYS_TO_BE_SKIPPED_IN_SAMPLE_METADATA = [
    "_id",
    "storage",
    "processed",
    "barCode",
    "plateProcessSteps",
    "plateComments",
    "comments",
    "labId",
    "puid",
    "status",
    "version",
    "color",
    "toBePicked",
    "plated",
    "plateHistory",
    "lastModifiedDate",
    "deliveryId",
    "creationDate",
];

export function prepareHistoryProcessSteps(
    processSteps: any[],
    forSample: boolean = false
): SampleHistoryProcessSteps[] {
    const historyProcessSteps = [];
    processSteps.forEach((process) => {
        if (!process?.status) {
            return;
        }
        historyProcessSteps.push(mapProcessToHistorySteps(process, forSample));
    });

    return historyProcessSteps;
}

function mapProcessToHistorySteps(
    process: Step,
    forSample: boolean = false
): SampleHistoryProcessSteps {
    if (process["action"].length == 0 || STEPS_WITH_NO_ACTION.includes(process["status"])) {
        return createProcessStepWithNoActions(process, forSample);
    } else {
        return createProcessStepWithActions(process, forSample);
    }
}

//For historical data created from the old processing service;
export const DEFAULT_DATE = "1971-01-01T00:00:00Z";

function createProcessStepWithNoActions(process: any, forSample = false): SampleHistoryProcessSteps {
    const processStep: SampleHistoryProcessSteps = new SampleHistoryProcessSteps();
    processStep.plateId = process.lastUpdatedPlateID;
    processStep.batchID = process.batchID;
    if (forSample) {
        processStep.position = process.position;
    }
    processStep.equipment = checkProcessEndTimeForSelection(process, process.startEquipments, process.endEquipments);
    processStep.reagents = checkProcessEndTimeForSelection(process, process.chemicals, process.endChemicals);
    processStep.puid = process.endTimeStamp === null || process.endTimeStamp === DEFAULT_DATE ? process.startPUID : process.endPUID;
    processStep.stepTime = process.endTimeStamp === null || process.endTimeStamp === DEFAULT_DATE ? convertUTCToLocal(process.startTimeStamp) : convertUTCToLocal(process.endTimeStamp);
    if (process["step"] === ProcessStep.NANODROP_FAIL) {
        processStep.stepAction = `${process["step"]},${process["status"]}`;
    } else {
        processStep.stepAction = process["status"];
    }
    return processStep;
}

function checkProcessEndTimeForSelection(process: any, startItems: any[], endItems: []): any[] {
    if (process.endTimeStamp === null && startItems?.length !== 0) {
        return startItems;
    } else if (process.endTimeStamp !== null && endItems?.length !== 0) {
        return endItems;
    } else {
        return [];
    }
}

function createProcessStepWithActions(process: any, forSample = false): SampleHistoryProcessSteps {
    const processStep: SampleHistoryProcessSteps = new SampleHistoryProcessSteps();
    processStep.plateId = process.lastUpdatedPlateID;
    processStep.batchID = process.batchID;
    if (forSample) {
        processStep.position = process.position;
    }
    processStep.stepAction = process["status"] + "," + process.action[process.action.length - 1].action;
    processStep.stepTime = convertUTCToLocal(process.action[process.action.length - 1].actionStartTime);
    const lastActionInProcessStep = process.action[process.action.length - 1];
    processStep.equipment = lastActionInProcessStep.equipments === null || lastActionInProcessStep.equipments?.length === 0 ? [] : lastActionInProcessStep.equipments;
    processStep.reagents = lastActionInProcessStep.chemicals === null || lastActionInProcessStep.chemicals?.length === 0 ? [] : lastActionInProcessStep.chemicals;
    processStep.puid = process.action[process.action.length - 1].actionPUID;
    return processStep;
}

export function convertUTCToLocal(date: Date): string {
    if (!date) {
        return "";
    }
    let offset = new Date().getTimezoneOffset();
    let dateInLocal = new Date(date);
    dateInLocal.setMinutes(dateInLocal.getMinutes() - offset);
    return dateInLocal.toISOString().split(".")[0].replace("T", " ");
}

export const PLATE_ID_LIST = [
    {label: "CURRENT", value: {type: PlateIdTypes.LAST_UPDATED_PLATEID, key: "lastUpdatedPlateID"}},
    {label: "SOURCE", value: {type: PlateIdTypes.SOURCE, key: "plateID"}},
    {label: "LYSIS", value: {type: PlateIdTypes.LYSIS, key: "lysisPlateID"}},
    {label: "MAGMIX", value: {type: PlateIdTypes.MAGMIX, key: "magMixPlateID"}},
    {label: "DNA", value: {type: PlateIdTypes.DNA, key: "dnaPlateID"}},
    {label: "ALIQUOT", value: {type: PlateIdTypes.ALIQUOT, key: "aliquotPlateID"}},
    {label: "WGA", value: {type: PlateIdTypes.WGA, key: "wgaPlateID"}},
    {label: "PGX", value: {type: PlateIdTypes.PGX, key: "pgxPlateID"}},
];

export const EXCEL_TYPE =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";

export type ProcessStepsForExcelExport = {
    name: string;
    processSteps: SampleHistoryProcessSteps[];
};

export const ALLOWED_STATUS_FOR_SAMPLE_TYPE_UPDATE = [
    PlateStatus.RECEIVED,
    PlateStatus.RECEPTION_COMPLETED,
    PlateStatus.QCSCAN_STARTED,
    PlateStatus.QCSCAN_COMPLETED,
];

